import toNumber from 'lodash/toNumber'
import { transparentize } from 'polished'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { Flex, Grid } from 'components/Layout'
import { disabledHover } from 'components/mixins'
import { H4, Text } from 'components/Typography'
import { useValidationErrorTrackingForGA4Provider } from 'providers/googleAnalytics/utils/ErrorTracking/useValidationErrorTrackingForGA4'
import {
  SPACE,
  FONT_SIZE,
  getTextColor,
  BORDER_WIDTH,
  COLOR,
  RADIUS,
  getBorderColor,
  HINT_HEIGHT,
  INPUT_SIZE,
} from 'Theme'

const ItemWrapper = styled(Flex)`
  background-color: ${COLOR.WHITE};
  border: ${BORDER_WIDTH.PX_1} solid ${({ borderColor }) => borderColor};
  border-radius: ${RADIUS.PX_6};
  line-height: 1.1;
  cursor: pointer;

  ${({ isSelected, isError, isDisabled }) =>
    !isSelected &&
    !isError &&
    !isDisabled &&
    `
    &:hover {
      border-color: ${COLOR.GRAYSCALE_4};
    }
  `}
  ${({ isDisabled }) =>
    isDisabled &&
    `
    background-color: ${COLOR.GRAYSCALE_6};
    color: ${transparentize(0.6, COLOR.GRAYSCALE_1)};
    border: none;
    ${disabledHover}
  `}
  ${({ isSelected, isError }) =>
    isSelected &&
    !isError &&
    `
    background-color: ${transparentize(0.9, COLOR.PRIMARY_1)};
  `}
`

const StyledText = styled(Text)`
  display: block;
  height: ${HINT_HEIGHT};
`

export const CardSelectGroup = ({
  name,
  onChange,
  children,
  label,
  isDisabled,
  isError,
  hint,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  useValidationErrorTrackingForGA4Provider({
    hint,
    isError,
  })
  return (
    <div>
      {label && <H4 mb={SPACE.PX_20}>{label}</H4>}
      <Grid
        gridTemplateColumns={{ TABLET: '1fr 1fr', DESKTOP: '1fr 1fr 1fr 1fr' }}
        gridGap={SPACE.PX_10}
        data-test-class={name}
      >
        {React.Children.map(children, (child, index) => {
          const isSelected = selectedIndex === index
          return (
            <ItemWrapper
              key={index}
              data-test-is-selected={isSelected}
              isSelected={isSelected}
              width="100%"
              height={INPUT_SIZE.M}
              borderColor={getBorderColor({
                isFocusedOrChecked: isSelected,
                isError: child.props.isErroable && isError,
              })}
              isError={isError}
              isDisabled={isDisabled}
              onFocus={() => {
                setSelectedIndex(index)
              }}
            >
              {React.cloneElement(child, {
                onClick: () => {
                  setSelectedIndex(index)
                  if (child.props.value) {
                    onChange(parseInt(child.props.value))
                  }
                },
                onBlur: (event) => {
                  const inputValue = toNumber(event.target.value)
                  onChange(inputValue)
                },
                ...child.props,
              })}
            </ItemWrapper>
          )
        })}
      </Grid>
      <StyledText
        fontSize={FONT_SIZE.PX_12}
        mt={SPACE.PX_5}
        color={getTextColor({ isError })}
        data-test-class={DATA_TEST_CLASS.CARD_SELECT_HINT}
      >
        {hint ?? ''}
      </StyledText>
    </div>
  )
}

CardSelectGroup.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  label: PropTypes.string,
  isError: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hint: PropTypes.string,
}
